// Ranks every insurer by how much it reimburses for one selected service
// (+ any selected add-ons), so the result screen can show the user's pick
// against the whole market (AHD-8461).
//
// Pure over the in-memory InsurerData tree — no I/O. Reuses getCoverage (with
// its membership fallback + null guard), calculateCoverageReimbursement and
// calculateReimbursement, so the ranking can never drift from the
// single-insurer number the form shows for the same selection.
//
// ponytail: ranks by per-check-up reimbursement (base service + selected
// add-ons) and carries `period` as metadata — it does NOT annualise across
// periods. "CHF back for this check-up" is the honest, comparable number; the
// data can't cleanly support a common timeframe.
import type { Coverage, InsurerData, Insurer } from "./insurance-types"
import {
  getCoverage,
  calculateCoverageReimbursement,
  calculateReimbursement,
  coverageCeiling,
  isEstimatedCoverage,
  sharesEnvelope,
} from "./insurers"
import { findService, membershipCoverageBase } from "./insurance-services"

export type RankStatus = "confirmed" | "likely" | "unknown" | "not_covered"

// The subset of an Addon this needs — the form passes full Addon objects.
export interface RankAddon {
  humanReadableId: string
  /** What the customer pays for this line — it lands in the totals. */
  price: number
  /**
   * The part of `price` an insurer's rule can actually pay on, when the two
   * differ. Only the membership uses it: a 2x tier costs CHF 499 but its
   * invoice itemises one Advanced blood test plus one follow-up panel
   * (lib/proforma/catalog.ts — "an N× membership bills N reports + N
   * panels"), and every stored blood-test rule reads "once per year". So the
   * customer pays 499 and the insurer's rule applies to the one panel.
   * Defaults to `price`, which is what every add-on wants.
   */
  coverageBase?: number
}

// Bucket order: confirmed amounts first, then likely, then explicit
// no-coverage, then not-yet-confirmed. Lower = higher up the list.
// "Not yet confirmed" sits last because it's the least useful row to a
// reader — we know nothing about it — while "Not covered" is a real answer
// (Ferdinand on INS-3, 2026-08-18).
const STATUS_RANK: Record<RankStatus, number> = {
  confirmed: 0,
  likely: 1,
  not_covered: 2,
  unknown: 3,
}

// The "Other" free-text option is not a plan anyone holds, so it never
// competes for a position: it sits under every real package whatever its
// bucket (Ferdinand on INS-3, 2026-08-18).
function isOtherPackage(packageId: string): boolean {
  return packageId.endsWith("-other")
}

function comparePackages(a: RankedPackage, b: RankedPackage): number {
  const byOther = Number(isOtherPackage(a.id)) - Number(isOtherPackage(b.id))
  if (byOther !== 0) return byOther
  return STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.chf - a.chf
}

export interface RankedPackage {
  id: string
  name: string
  chf: number
  status: RankStatus
  period?: string
}

export interface RankedInsurer {
  id: string
  name: string
  logo: Insurer["logo"]
  status: RankStatus
  /** Reimbursement (base + add-ons) of the insurer's best package — or of the
   * user's own package for their own insurer. 0 for unknown/not_covered. */
  chf: number
  /** chf as a % of the total price (service + selected add-ons); 0 when price unknown. */
  pct: number
  /** period of the best package, e.g. "annual" | "3_years". */
  period?: string
  bestPackageName: string | null
  /** Every package, best-first (status bucket, then chf desc). */
  packages: RankedPackage[]
  /** Set only on the user's own insurer, and only when one of its OTHER plans
   * reimburses more than the plan they selected. Pinning their row to their own
   * plan (see rankInsurers) would otherwise bury "you're on the wrong tier". */
  betterPlan?: { name: string; chf: number }
}

// Add-on reimbursement for one package: each selected add-on resolved by its
// humanReadableId (the same CMS coverage key the form uses).
//
// `anyConfirmed` tracks whether at least one PAYING add-on is insurer-confirmed
// (fixed/percentage rather than estimated). An add-on can be the only thing that
// pays out: a SWICA customer on Core MRI (not covered) who adds Mamma-MRI
// (covered in full) has confirmed coverage for the Mamma-MRI, so the bucket has
// to follow the add-on rather than the base.
//
// Shared with coverage-form.tsx so the panel's own total can't drift from the
// ranking — this used to be duplicated as a reducer there.
export function addonReimbursement(
  data: InsurerData,
  insurerId: string,
  packageId: string,
  addons: readonly RankAddon[],
  /**
   * The base service's allowance and what it has already claimed against it,
   * when the base pays out. A line resolving to the SAME allowance draws on
   * the headroom that is left instead of being granted a second full one —
   * which is what a membership tier does, since `getCoverage` sends a
   * `membership-*` id to the package's blood-test rule. At Atupri that is one
   * "90%, max. CHF 200 per year", so a tier bought alongside a check-up used
   * to report CHF 400 against a CHF 200 allowance.
   *
   * Omitted → each line is capped on its own, which is right when there is no
   * base payout to share with (a not_covered base carrying an add-on payout).
   */
  base?: { coverage: Coverage; paid: number }
): { chf: number; anyConfirmed: boolean } {
  const { chf, anyConfirmed } = addons.reduce<{
    chf: number
    anyConfirmed: boolean
    claimed: number
  }>(
    (acc, a) => {
      const cov = getCoverage(data, insurerId, packageId, a.humanReadableId)
      if (!cov) return acc
      const own = calculateReimbursement(cov, a.coverageBase ?? a.price) ?? 0
      const ceiling =
        base && sharesEnvelope(base.coverage, cov)
          ? coverageCeiling(cov)
          : null
      const chf =
        ceiling != null ? Math.min(own, Math.max(0, ceiling - acc.claimed)) : own
      if (chf <= 0) return acc
      return {
        chf: acc.chf + chf,
        anyConfirmed: acc.anyConfirmed || !isEstimatedCoverage(cov),
        claimed: acc.claimed + chf,
      }
    },
    { chf: 0, anyConfirmed: false, claimed: base?.paid ?? 0 }
  )
  return { chf, anyConfirmed }
}

function rankPackage(
  data: InsurerData,
  insurerId: string,
  pkg: { id: string; name: string },
  serviceId: string,
  servicePrice: number,
  addons: readonly RankAddon[]
): RankedPackage {
  const cov = getCoverage(data, insurerId, pkg.id, serviceId)
  if (!cov)
    return {
      id: pkg.id,
      name: pkg.name,
      chf: 0,
      status: "unknown",
    }
  if (cov.type === "not_covered") {
    // Add-ons can pay out on a base that doesn't, so the base's own coverage
    // type is not enough to decide the bucket. No allowance is passed: a
    // not_covered base grants none to share.
    const addon = addonReimbursement(data, insurerId, pkg.id, addons)
    // No add-on payout either → genuinely no coverage.
    if (addon.chf <= 0) {
      return {
        id: pkg.id,
        name: pkg.name,
        chf: 0,
        status: "not_covered",
        period: cov.period,
      }
    }
    // The add-on is the whole payout, so the bucket follows the add-on's own
    // coverage — NOT the not_covered base. Returning `not_covered, chf: 0` here
    // sorted a paying plan below plans that reimburse less (STATUS_RANK buckets
    // before amount), so the comparison could recommend the wrong insurer.
    return {
      id: pkg.id,
      name: pkg.name,
      chf: addon.chf,
      status: addon.anyConfirmed ? "confirmed" : "likely",
      period: cov.period,
    }
  }
  const base = calculateCoverageReimbursement(cov, serviceId, servicePrice) ?? 0
  // Lines drawing on the base's own allowance may only claim the headroom it
  // leaves — a membership tier does, which is why the base is handed over.
  const addon = addonReimbursement(data, insurerId, pkg.id, addons, {
    coverage: cov,
    paid: base,
  })
  const chf = base + addon.chf
  return {
    id: pkg.id,
    name: pkg.name,
    chf,
    // Same split the result panel uses: only fixed/percentage are
    // insurer-confirmed. `itemized` reimburses just some line items, so it's an
    // estimate and ranks in the `likely` bucket — it used to rank `confirmed`
    // while separately carrying a "needs confirmation" chip, which let the
    // comparison list contradict the panel above it.
    status: isEstimatedCoverage(cov) ? "likely" : "confirmed",
    period: cov.period,
  }
}

// "Best" for the headline row — the same ordering the expanded list uses, so
// the row and the list below it can't disagree about which plan wins.
function betterPackage(a: RankedPackage, b: RankedPackage): RankedPackage {
  return comparePackages(a, b) <= 0 ? a : b
}

export function rankInsurers(
  data: InsurerData,
  serviceId: string,
  addons: readonly RankAddon[] = [],
  // The user's own selection. Their row is pinned to the plan they actually
  // picked instead of their insurer's most generous one, so the comparison can
  // never contradict the result panel above it (e.g. Atupri Mivita Reala pays
  // 90% capped at CHF 200 = CHF 200 on a CHF 299 blood test, while Mivita
  // Extensa's higher cap pays CHF 269 — the row used to show 269 next to a
  // panel reading 200). Every plan still appears in the expanded list.
  userSelection?: { insurerId: string; packageId: string },
  /** What the visitor pays for `serviceId`, when that differs from the list
   *  price (the member rate a selected membership tier earns). It has to reach
   *  here as well as into computePanelResult: these rows are reimbursements on
   *  the same invoice, and pricing them off the list price while the panel
   *  above uses the member price is exactly the #2247 drift the pinning below
   *  exists to prevent. */
  servicePrice?: number
): RankedInsurer[] {
  const service = findService(serviceId)
  if (!service) return []
  const price = servicePrice ?? service.price
  // Same split as the panel above: a membership picked on its own is claimed
  // on the one panel its bundle invoices, not on the tier price. Keeping both
  // on one rule is what stops these rows disagreeing with the figure above
  // them.
  const coverageBase = membershipCoverageBase(serviceId) ?? price
  const totalPrice = price + addons.reduce((s, a) => s + a.price, 0)

  const ranked: RankedInsurer[] = data.insurers.map((ins) => {
    const ranked = ins.packages
      .map((p) => rankPackage(data, ins.id, p, serviceId, coverageBase, addons))
      .sort(comparePackages)

    // An insurer that told us it doesn't cover screenings is not_covered
    // regardless of its package rows — same rule as the single-insurer flow.
    // Push that down to every plan so the expanded list reads "not covered"
    // too, not "not yet confirmed".
    const packages: RankedPackage[] = ins.confirmedNoCoverage
      ? ranked.map<RankedPackage>((p) => ({
          ...p,
          chf: 0,
          status: "not_covered",
        }))
      : ranked

    const pinned =
      userSelection?.insurerId === ins.id
        ? packages.find((p) => p.id === userSelection.packageId)
        : undefined

    const insurerBest = ins.confirmedNoCoverage
      ? null
      : packages.reduce<RankedPackage | null>(
          (acc, p) => (acc ? betterPackage(acc, p) : p),
          null
        )

    const best = ins.confirmedNoCoverage ? null : (pinned ?? insurerBest)

    // "Your plan pays 200, but their Extensa tier pays 269" — surfaced inline on
    // the user's row so pinning it to their own plan doesn't hide the upgrade.
    const betterPlan =
      pinned && insurerBest && insurerBest.chf > pinned.chf
        ? { name: insurerBest.name, chf: insurerBest.chf }
        : undefined

    const status: RankStatus = ins.confirmedNoCoverage
      ? "not_covered"
      : (best?.status ?? "unknown")
    const chf = status === "confirmed" || status === "likely" ? best!.chf : 0

    return {
      id: ins.id,
      name: ins.name,
      logo: ins.logo,
      status,
      chf,
      pct: totalPrice > 0 ? Math.round((chf / totalPrice) * 100) : 0,
      period: best?.period,
      bestPackageName: best?.name ?? null,
      packages,
      betterPlan,
    }
  })

  // Stable sort (V8): bucket, then reimbursement desc; ties keep CMS displayOrder.
  return ranked.sort(
    (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.chf - a.chf
  )
}
